import { StringParam, useQueryParam, withDefault } from 'use-query-params'

/**
 * Validates a deep-link url before it is used as a redirect target.
 *
 * @param {String} deeplink - raw, still url-encoded value
 * @returns {String|undefined} The decoded link, or undefined when it is not allowed
 */
export function validateDeepLink(deeplink) {
  if (!deeplink) return undefined

  let link
  try {
    link = decodeURIComponent(deeplink)
  } catch {
    // `?to=%ZZ` throws URIError, and this runs during render — an escaping throw is a blank screen
    return undefined
  }

  // Rejected, not stripped: the URL parser removes tab/LF/CR before normalising `\` to `/`, so
  // `/<TAB>/host/x` looks relative here but reaches the browser as protocol-relative `//host/x`
  if (/[\t\n\r]/.test(link)) return undefined

  // Same-origin path. `//host/x` and `/\host/x` are protocol-relative, so they fall through
  if (link.startsWith('/') && !/^\/[/\\]/.test(link)) return link

  let url
  try {
    url = new URL(link, window.location.origin)
  } catch {
    return undefined
  }

  // Host comes from the parser, never a regex: one that stops at the first `/` misses `?` and
  // `#` as terminators, so `https://evil.com#x.qcareplus.com` would read as a trusted host
  if (url.protocol !== 'https:') return undefined
  if (!/(^|\.)qcareplus\.com$/i.test(url.hostname)) return undefined

  return link
}

/**
 * Hook to get the redirect url when coming from other for deep-links.
 *
 * When a user follows a deep-link and requires authentication, an additional query param named to
 * is added to the url. ex: https://patient.qcareplus.com/redirect?to=https://rails.qcareplus.com/labs/010101.
 *
 * @returns {String} The url to redirect to
 */
export default function useQueryDeepLink() {
  const [deeplink] = useQueryParam('to', withDefault(StringParam, null), { updateType: 'replaceIn' })

  return validateDeepLink(deeplink)
}

export const postConsentRedirectKey = 'post-consent-redirect'
